import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn, Button, ScrollArea } from '@scio3/ui';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { SETTINGS_ACCESS_DENIED_TOAST_ID } from './app-shell-route-utils';
import { useUserManagementAccess } from './useUserManagementAccess';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useReferralAccess } from '@/features/referral/hooks/useReferralAccess';
import { useCanManageTags } from '@/features/tags/hooks/useCanManageTags';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { SCIO1_STATE_MAP } from '@/components/iframe/scio1-route-map';
import { LAPSED_SETTINGS_NAV_PATHS } from '@/features/billing/lapsed/lapsed-settings-paths';
import type { LucideIcon } from 'lucide-react';

import { PlanGate } from '@/components/shared';
import { ScrollContainer } from '@/components/shared/ScrollContainer';
import type { PlanGateProps } from '@/components/shared';
import type { getCompanyFeatures } from '@/hooks/useCompanyFeatures';
import {
  UserCircle,
  Building2,
  Users,
  Gift,
  CreditCard,
  Wallet,
  Receipt,
  Building,
  Ticket,
  ShoppingCart,
  ClipboardList,
  KeyRound,
  SlidersHorizontal,
  Calendar,
  CheckSquare,
  Database,
  Rss,
  Link,
  BarChart3,
  Megaphone,
  Bell,
  Radio,
  MapPin,
  Key,
  Plug,
  Shield,
  Lock,
  Fingerprint,
  ScrollText,
  FileSignature,
  Tags,
} from 'lucide-react';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Controls sidebar visibility. Undefined = always visible. */
  show?: boolean;
  /**
   * Direct-URL access gate — checked when the page is accessed via URL.
   * - undefined: falls back to `show` (hidden from sidebar = blocked via URL)
   * - boolean: overrides `show` for access control (e.g. hidden from sidebar but accessible if permission passes)
   */
  accessGate?: boolean;
  /**
   * Renders the item with the referral "Refer & Earn" treatment (Var D): a
   * gradient gift chip in place of the plain icon + a trailing teal "$25" pill.
   * Used for the single item in the REWARDS group.
   */
  referralChip?: boolean;
  /** Trailing pill text (teal "$25" pill on the referral row). */
  pill?: string;
  /**
   * Paywall treatment — the item still shows + navigates (the page renders its PlanGate upgrade
   * prompt via SETTINGS_PLAN_GATES), but the row gets a trailing lock icon + a muted plan badge
   * (`lockBadge`). Mirrors the Claude "Groups 🔒 Enterprise" locked nav row.
   */
  locked?: boolean;
  /** Plan-tier badge shown next to the lock when `locked` (e.g. "Pro Plus"). */
  lockBadge?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Routes that render native React components (not iframe).
 *  Also used by AppShell to decide when to mount the scio1 modal adapter iframe. */
export const NATIVE_SETTINGS_SLUGS = new Set<string>();
if (import.meta.env.VITE_USE_NATIVE_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('personal-profile');
if (import.meta.env.VITE_USE_NATIVE_COMPANY_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('company-profile');
if (import.meta.env.VITE_USE_NATIVE_EXTERNAL_COMS === 'true')
  NATIVE_SETTINGS_SLUGS.add('external-coms');
if (import.meta.env.VITE_USE_NATIVE_PREFERENCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('preference-settings');
if (import.meta.env.VITE_USE_NATIVE_APIKEYS === 'true') NATIVE_SETTINGS_SLUGS.add('apikeys');
if (import.meta.env.VITE_USE_NATIVE_FEEDS_ADMIN === 'true')
  NATIVE_SETTINGS_SLUGS.add('feeds-admin');
NATIVE_SETTINGS_SLUGS.add('tags');
if (import.meta.env.VITE_USE_NATIVE_DATASOURCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('datasources-admin');
if (import.meta.env.VITE_USE_NATIVE_POP_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('proof-of-play-settings');
// Broadcast is native-only — no env flag, no scio1 fallback.
NATIVE_SETTINGS_SLUGS.add('broadcasts');
if (import.meta.env.VITE_USE_NATIVE_INTEGRATIONS === 'true')
  NATIVE_SETTINGS_SLUGS.add('integrations');
if (import.meta.env.VITE_USE_NATIVE_BILLING === 'true') {
  NATIVE_SETTINGS_SLUGS.add('subscription-plan');
  NATIVE_SETTINGS_SLUGS.add('payment');
  NATIVE_SETTINGS_SLUGS.add('payment-history');
  // team-billing requires VITE_USE_NATIVE_TEAMS too — see below.
  NATIVE_SETTINGS_SLUGS.add('coupons');
  NATIVE_SETTINGS_SLUGS.add('payment-intent');
  NATIVE_SETTINGS_SLUGS.add('confirm-linked-account');
  NATIVE_SETTINGS_SLUGS.add('order-device');
  NATIVE_SETTINGS_SLUGS.add('ordered-device');
}
if (
  import.meta.env.VITE_USE_NATIVE_BILLING === 'true' &&
  import.meta.env.VITE_USE_NATIVE_SUB_ACCOUNT_BILLING === 'true'
)
  NATIVE_SETTINGS_SLUGS.add('sub-account-billing');
// Team Billing is gated by both flags so VITE_USE_NATIVE_BILLING=true +
// VITE_USE_NATIVE_TEAMS=false falls through to scio1 iframe at this slug
// (target state: app.account.teamBillingScio3, see SCIO1_STATE_MAP).
if (
  import.meta.env.VITE_USE_NATIVE_BILLING === 'true' &&
  import.meta.env.VITE_USE_NATIVE_TEAMS === 'true'
)
  NATIVE_SETTINGS_SLUGS.add('team-billing');
if (import.meta.env.VITE_USE_NATIVE_ACCOUNT_MEMBERS === 'true')
  NATIVE_SETTINGS_SLUGS.add('account-members');
// Group Security v3 pages are always native (no scio1 equivalent); runtime-gated by folderLevelSecurity + role.
NATIVE_SETTINGS_SLUGS.add('group-security');
// The Users page is `users` (renamed from `members`, 2026-10-16); `members` stays for its redirect.
// Same trap as `teamspaces` below: without this line the page went to the scio1 frame (blank).
NATIVE_SETTINGS_SLUGS.add('users');
NATIVE_SETTINGS_SLUGS.add('members');
// The route is `teamspaces` (renamed from `workspaces` in b4a1ca1047). `isNativePage` keys on the
// URL slug, and the rename moved the router entry, the nav row and the tests but NOT this line — so
// a page declared three lines up as having "no scio1 equivalent" was handed to the scio1 iframe,
// and with VITE_DISABLE_SCIO1=true that renders "This page is not available yet". Nothing failed
// until someone opened it with scio1 off, which is why it survived the rename.
// `workspaces` stays: /app/s/workspaces is still a live redirect to /app/s/teamspaces.
NATIVE_SETTINGS_SLUGS.add('teamspaces');
NATIVE_SETTINGS_SLUGS.add('workspaces');
NATIVE_SETTINGS_SLUGS.add('roles');
if (import.meta.env.VITE_USE_NATIVE_SAML_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('saml-settings');
if (import.meta.env.VITE_USE_NATIVE_SHARE_LINKS_ADMIN === 'true')
  NATIVE_SETTINGS_SLUGS.add('share-links-admin');
if (import.meta.env.VITE_USE_NATIVE_OPS_SCHEDULE === 'true')
  NATIVE_SETTINGS_SLUGS.add('operational-schedule');
if (import.meta.env.VITE_USE_NATIVE_MONITORING_ALERT_RULES === 'true')
  NATIVE_SETTINGS_SLUGS.add('monitoring-alert-rules');
if (import.meta.env.VITE_USE_NATIVE_FUNCTIONAL_LOCATIONS === 'true')
  NATIVE_SETTINGS_SLUGS.add('functional-locations');
if (import.meta.env.VITE_USE_NATIVE_BRANDING_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('branding-settings');
// Referral "Refer & Earn" page — native (no scio1 equivalent) wherever the
// referral engine is wired (VITE_REFERRAL_API_URL). The enable flag
// (VITE_ENABLE_REFERRAL_ENGINE) only gates the UI entry points (sidebar item /
// pill / account-menu); the page itself stays reachable by direct URL even when
// the flag is off — for testing / soft-launch.
if (import.meta.env.VITE_REFERRAL_API_URL) NATIVE_SETTINGS_SLUGS.add('referral');
// Revue is always native — no scio1 equivalent.
NATIVE_SETTINGS_SLUGS.add('revue');

/** Company feature flags type — derived from getCompanyFeatures return. */
type CompanyFeatures = ReturnType<typeof getCompanyFeatures>;

/** PlanGateProps with planFeature narrowed to actual feature flag keys. */
type SettingsPlanGateConfig = Omit<PlanGateProps, 'planFeature'> & {
  planFeature: keyof CompanyFeatures;
};

/**
 * Settings slugs that show a native plan gate when their feature flag is disabled.
 *
 * Only includes iframe-only pages that lack a scio1 plan gate.
 * - API Keys & Data Sources: handled by their own native page components
 * - SAML & Monitoring Alerts: scio1 handles plan gating inside the iframe
 */
export const SETTINGS_PLAN_GATES: Record<string, SettingsPlanGateConfig> = {
  'approval-list': {
    planFeature: 'approvalWorkflow',
    minPlanRequired: 'proPlus',
    featureLabel: 'Approval Requests',
    planLabel: 'Pro Plus Plan',
  },
  // User Management (Group Security v3) — Teamspaces / Roles / Groups are a Pro Plus feature gated on
  // `allowTeams`. When the plan lacks it, the page shows the upgrade prompt (and the sidebar row is
  // locked). Users/Members stays open on every plan. See the User Management nav group below.
  workspaces: {
    planFeature: 'allowTeams',
    minPlanRequired: 'proPlus',
    featureLabel: 'Teamspaces',
    planLabel: 'Pro Plus Plan',
  },
  // `roles` is deliberately NOT here (Ted, 2026-09-02). A SETTINGS_PLAN_GATES entry replaces the
  // whole Outlet with the overlay, and below plan the Roles page must still SHOW the six system
  // roles read-only — only the CUSTOM-role half is paywalled. RolesPage owns that split and scopes
  // its own PlanGate to the custom section (same technique as GroupsPlanUpsell).
  // `group-security` is deliberately NOT here either (scio3#2413). A SETTINGS_PLAN_GATES entry
  // replaces the whole Outlet, and `GroupSecurityPage` models FOUR states that the single
  // `allowTeams` test here cannot express: plan upsell, "activated but not enforceable", the
  // activation prompt, and the real UI. It also carries a grandfather clause —
  // `showPlanUpsell = !allowTeams && !folderLevelSecurity`, so an account whose switch is genuinely
  // ON keeps the management UI whatever the plan flag says — and this entry made that code
  // UNREACHABLE, because the Outlet was hidden before the page could run. The page owns its own
  // paywall, exactly as `roles` does above.
};

/** Settings slugs that render without the sidebar (full-width / standalone pages).
 *  Matches the slug exactly OR as a parent of the current path (so `order-device/:alias` also hides). */
const FULL_WIDTH_SETTINGS_SLUGS = new Set<string>([
  'payment-intent',
  'payment-thankyou',
  'order-device',
  // Broadcast is a first-class page in the Audit Log shape: the Settings entry stays, the page
  // takes the whole width (Henry, 2026-09-29). Kept under /app/s/ so delivered-message deep
  // links (`?broadcastId=…`) and the Screens-page promo keep working unchanged.
  'broadcasts',
]);

/**
 * Native settings pages that own their scrolling — a fixed toolbar, a scrolling table and a
 * fixed pagination footer, the Audit Log shape. They render straight into the card, without
 * the ScrollContainer: inside it a fixed-height page still scrolls by the OptiBot FAB gutter
 * the container pads in, and the footer drifts off the bottom.
 */
const PAGE_OWNS_SCROLL_SLUGS = new Set<string>(['broadcasts']);

/** scio1-only slugs that redirect to a scio3 equivalent (relative to /app/s/). */
const SETTINGS_REDIRECTS: Record<string, string> = {
  'android-devices': '/app/s/order-device',
  'free-devices': '/app/s/order-device',
  'subscription-thankyou': '/app/s/personal-profile',
  'subscription-init-thankyou': '/app/s/personal-profile',
  'proof-of-play-redirect': '/app/s/proof-of-play-settings',
  'request-apis': '/app/s/personal-profile',
};

function useSettingsNav(): NavGroup[] {
  const features = useCompanyFeatures();
  // Group Security v5 §4e — `can()` is UNIONED with each existing role gate, never a replacement:
  // `can(key) || <role gate>` can only ADD an entry for a user who holds the key, so nobody loses a
  // page they see today. Mirrors the backend's `@RequirePermission(key, { fallbackRoles })`.
  // A permission opens the ROLE half ONLY — every plan/feature/white-label clause still runs, so a
  // key can never buy an entitlement the account has not paid for.
  // CONFIG/editor pages deliberately take `.manage`, not `.view`: a read grant must not reveal a
  // settings editor (see Proof of Play + Monitoring Alerts below).
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin, canAccessBilling, currentTeam, can } =
    usePermissions();
  // Read the shared flag rather than re-deriving the role here: `useUserManagementAccess` is the
  // ONE rule for every User Management surface, and two copies of it is how #2351 shipped a gate
  // that disagreed with itself.
  const { usersPage } = useUserManagementAccess();
  // The Users page gate, shared with the top-bar shortcuts so the two cannot disagree (#2351).
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  // White-label sub-flags (scio1 account.html parity) — billing/subscription/PoP gate on the
  // domain `hide*` flags, not on isWhiteLabel itself.
  const hideSubscriptionPages = !!me?.brandingData?.hideSubscriptionPages;
  const hidePaymentHistoryPages = !!me?.brandingData?.hidePaymentHistoryPages;
  // OptiSigns-branded surfaces (Data Sources, Share Links, Integrations) show only when OptiSigns
  // branding is kept — hidden on white-label accounts that remove OptiSigns branding.
  const removeOptiSignsBranding = !!me?.brandingData?.removeOptiSignsBranding;
  const hideProofofPlay = !!me?.brandingData?.hideProofofPlay;
  const hideAccountMembersPage = !!me?.brandingData?.hideAccountMembersPage;
  const hideAuditLogging = !!me?.brandingData?.hideAuditLogging;
  const hideMonitorAndAlert = !!me?.brandingData?.hideMonitorAndAlert;
  const hideExternalCommunications = !!me?.brandingData?.hideExternalCommunications;
  // QC #1272 — hide Sub-Account Billing for accounts that are themselves a
  // child/linked sub-account. Backend already rejects activation with
  // "Cannot enable on child account"; hiding the tab mirrors scio1 and
  // avoids the dead-end click. parentAccountId/linkedAccountId are fetched
  // by GET_COMPANY_INFO (auth.ts) so this is populated at login time.
  const isChildAccount = !!company?.parentAccountId || !!company?.linkedAccountId;
  // scio1: isRootOwnerAndDefaultTeam() = isRootAdmin && (teamId == '1' || !teamId)
  const { isDefaultTeam } = useTeamStore();
  const isRootAdminDefaultTeam = isRootAdmin && isDefaultTeam;
  const canApprove = isTeamAdmin || !!currentTeam?.permissions?.playlistCanApprove;
  const { canAdministerTags } = useCanManageTags();
  const { entryEnabled, pageAccessible, loading: referralLoading } = useReferralAccess();
  // #2452 release cohort — decides Account Members vs the Directory group. See both below.
  const cohort = useGroupSecurityCohort();
  // Lapsed account — see the trim at the end of this function.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);

  const groups: NavGroup[] = [
    {
      label: 'Account',
      items: [
        { icon: UserCircle, label: 'Personal Profile', path: '/app/s/personal-profile' },
        {
          icon: Building2,
          label: 'Company Settings',
          path: '/app/s/company-profile',
          // #2452 scan finding 3 — capability arm is ON-cohort only; `company.view` is a MEMBER
          // BASELINE key, so on a legacy account it is true for every member and this row (and
          // the URL behind it) opened for the whole company. main gates it on isRootAdmin.
          show: ((cohort && can('company.view')) || isRootAdmin) && !isWhiteLabel, // scio1: hidden for white-label
        },
        {
          icon: Users,
          label: 'Account Members',
          path: '/app/s/account-members',
          // #2452 — THE OFF COHORT'S user-management row, and the exact inverse of the Directory
          // group below: an account sees one or the other, never both and never neither.
          //
          // Group Security v3 had hidden this unconditionally (`show: false`) because the split
          // menus replaced it. That is true for accounts on the new model; for every account that
          // predates the release it removed their user management and gave them a Directory they
          // did not ask for. The row comes back for them — the page was only unlinked, not deleted.
          //
          // The scio1 `hideAccountMembersPage` branding gate still applies on top, unchanged.
          show: !cohort,
          accessGate: !hideAccountMembersPage,
        },
      ],
    },
    {
      // DIRECTORY (scio3#2412) — Users / Teamspaces / Roles / Groups, gated on folderLevelSecurity
      // (the additive base for group grants). Manage matrix: Users/Roles =
      // Owner/Super-Admin/Admin/Team-Owner; Teamspaces/Groups = Owner/Super-Admin only.
      //
      // It was "User Management" while only admins could reach it. Three of these four pages are now
      // deliberately viewable by people who manage nothing — Teamspaces on folderLevelSecurity alone,
      // Roles and Groups read-only for a plain User (RO-12 / GP-12) — and Users is open to teamspace
      // admins scoped to their own teamspaces (#2351). A heading promising "Management" to someone
      // whose every control is hidden is the same defect the Teamspaces page header had.
      // "Directory" is true for both audiences, is the conventional name for this grouping (Google
      // Workspace, Okta), and separates the PLACE from the `User` ROLE — "Directory → Users" reads as
      // the list of people, "User Management → Users" as managing the User role.
      // #2452 — the whole group is ON-COHORT ONLY. Every item carries `cohort` (a group with no
      // visible items renders nothing, so this hides the heading too). The inverse row is Account
      // Members above: exactly one of the two is ever drawn.
      //
      // ⚠ `accessGate: true` on all four is LOAD-BEARING, not boilerplate. Without it the fallback
      // on line ~732 reads `show === false` as "access denied", and for an OFF-cohort account the
      // `useEffect` below fires FIRST — toasting "You do not have permission to access this page"
      // and replacing the URL with `/app/screenManagement`, while `if (isAccessDenied) return null`
      // withholds the `<Outlet>` so `GroupSecurityCohortRoute` never mounts and never gets to send
      // the user to Account Members. Measured 2026-10-15: all four Directory URLs landed on Screens
      // for a legacy account, which is precisely the outcome that route exists to prevent.
      //
      // The distinction the fallback cannot make: for the OFF cohort these pages are not FORBIDDEN,
      // they are SUPERSEDED. The redirect belongs to the route layer, which knows where the
      // equivalent page is; this layout only knows how to say no. So these four opt out of its gate
      // and let the route decide — `members` additionally keeps its `UserManagementRouteGuard`
      // (`area: 'usersPage'`), so the ON-cohort permission check is unchanged.
      label: 'Directory',
      items: [
        {
          // Members is open on EVERY plan (like Claude's "Members" row) — no lock — but it is
          // OWNER / SUPER ADMIN only (Isabel, 2026-10-12), which reverses Kati's 2026-10-08
          // decision to show it to every role.
          //
          // ⚠ This re-opens the single exception to the file's invariant that a permission may ADD
          // a page and never remove one. It is an exception on purpose and it has to be one: the
          // flag ALSO guards the `/app/s/users` route, so leaving `show: true` would render a
          // visible, unlocked row that silently redirects — which is #2413, filed against exactly
          // that shape. A row that cannot be opened must not be drawn.
          //
          // Not `locked` either: a lock means "upgrade to unlock" everywhere else in this file, and
          // no plan buys this. The row is absent rather than teasing.
          icon: Users,
          label: 'Users',
          path: '/app/s/users',
          // `usersPage` is already false for the OFF cohort (useUserManagementAccess), so `cohort`
          // is redundant here — stated anyway so this row reads the same as its three siblings and
          // survives any future change to that hub.
          show: cohort && usersPage,
          accessGate: true, // route-owned — see the group note above
        },
        {
          // Teamspaces / Roles / Groups are a Pro Plus feature (`allowTeams`). Always visible, but
          // LOCKED (lock icon + "Pro Plus" badge) when the plan lacks it — clicking opens the page's
          // upgrade prompt (SETTINGS_PLAN_GATES above). Mirrors Claude's "Groups 🔒 Enterprise" row.
          //
          // The icon/label used to FLIP to Users when `usersPage` was false (#2351): with the Users
          // row hidden, this row borrowed its name and rendered the roster instead. Now that every
          // role gets its own Users row, the flip would put TWO rows called "Users" on screen. One
          // row, one name — this is Teamspaces for everybody.
          icon: Building2,
          label: 'Teamspaces',
          path: '/app/s/teamspaces',
          show: cohort,
          accessGate: true, // route-owned — see the group note above
          locked: !features.allowTeams,
          lockBadge: 'Pro Plus',
        },
        {
          // Groups before Roles (Jessica, 2026-10-16).
          icon: Shield,
          label: 'Groups',
          path: '/app/s/group-security',
          show: cohort,
          accessGate: true, // route-owned — see the group note above
          locked: !features.allowTeams,
          lockBadge: 'Pro Plus',
        },
        {
          icon: KeyRound,
          label: 'Roles',
          path: '/app/s/roles',
          show: cohort,
          accessGate: true, // route-owned — see the group note above
          locked: !features.allowTeams,
          lockBadge: 'Pro Plus',
        },
      ],
    },
    {
      // "Refer & Earn" lives in its own REWARDS section (right after ACCOUNT)
      // rather than buried under Billing & Orders — it's a growth surface, not a
      // billing page. The row gets the gradient gift chip + teal "$25" pill
      // treatment (Var D) so it matches the account-dropdown promo card.
      label: 'Rewards',
      items: [
        {
          icon: Gift,
          label: 'Refer & Earn',
          path: '/app/s/referral',
          show: entryEnabled, // sidebar entry only when the flag is on
          // Defer the real subscription gate to ReferralPage while company data is still
          // loading — there's a transient where me is truthy but company is null, which
          // makes pageAccessible false even for paid subscribers. Treating accessGate as
          // true during loading prevents a premature redirect before company resolves.
          accessGate: referralLoading ? true : pageAccessible,
          referralChip: true,
          pill: 'Get $25',
        },
      ],
    },
    {
      label: 'Billing & Orders',
      items: [
        {
          icon: CreditCard,
          label: 'Subscription Plan',
          // Root admins OR the Billing role (D4) — the backend gates these on @RequireRole(BILLING_ROLES).
          path: '/app/s/subscription-plan',
          show: (can('billing.view') || canAccessBilling) && !hideSubscriptionPages, // scio1: gated on hideSubscriptionPages
        },
        {
          icon: Wallet,
          label: 'Payment Settings',
          path: '/app/s/payment',
          show: (can('billing.manage') || canAccessBilling) && !hideSubscriptionPages,
        },
        {
          icon: Receipt,
          label: 'Billing History',
          path: '/app/s/payment-history',
          // Nothing on the Subscription Plan page links to `/app/s/payment-history`, so this
          // sidebar entry is the way in — a payments list should not be two menus deep. If a
          // Subscription Plan redesign lands with its own entry, re-hide it HERE and check that
          // link exists first.
          // The PREDICATE is the v5 capability union, matching the two Billing rows above — main
          // carries `isRootAdmin` only because the capability plane does not exist there, and its
          // change is about WHICH ARM shows the row, not about narrowing who may see it.
          show: (can('billing.view') || canAccessBilling) && !hidePaymentHistoryPages, // scio1: gated on hidePaymentHistoryPages
        },
        {
          icon: Building,
          label: 'Sub-Account Billing',
          path: '/app/s/sub-account-billing',
          show: (can('billing.manage') || isRootAdmin) && !isChildAccount && !isWhiteLabel, // scio1: hidden for white-label
          accessGate: (can('billing.manage') || isRootAdmin) && !isChildAccount && !isWhiteLabel,
        },
        {
          icon: Building,
          // Phase 11 relabel: "Team Billing" → "Workspace Billing" when the account
          // uses the Workspace label. Sidebar entry is hidden (show:false) but the
          // label is still used on the deep-link page, so keep it noun-aware.
          label: 'Teamspace Billing',
          path: '/app/s/team-billing',
          // Sidebar entry intentionally hidden — the page is deep-link-only,
          // reached via the per-team "Team Billing" button on Account Members
          // (`/app/s/team-billing?teamId=<id>`). The old list view was a temp
          // test surface and is no longer exposed.
          show: false,
          // Direct URL access still allowed for Root Super Admin OR any team
          // admin/owner. Per-team data access is enforced inside the page via
          // backend `getTeamBillingInfo` (filters by team membership).
          accessGate: (isRootSuperAdmin || isTeamAdmin) && features.teamBilling,
        },
        {
          icon: Ticket,
          label: 'Coupons',
          path: '/app/s/coupons',
          // scio1 parity: topNavigation.html:178-181 — `isRootOwnerAndDefaultTeam() && !isWhiteLabel`.
          // Despite v1's misleading name, principal.js:64-67 evaluates `isRootAdmin + default team`.
          show: (can('billing.manage') || isRootAdminDefaultTeam) && !isWhiteLabel,
          accessGate: (can('billing.manage') || isRootAdminDefaultTeam) && !isWhiteLabel,
        },
        {
          icon: ShoppingCart,
          label: 'Order Devices',
          path: '/app/s/order-device',
          show: (can('orders.create') || isRootAdminDefaultTeam) && !isWhiteLabel,
        },
        {
          icon: ClipboardList,
          label: 'Order History',
          path: '/app/s/ordered-device',
          show: (can('orders.view') || isRootAdminDefaultTeam) && !isWhiteLabel,
        },
      ],
    },
    {
      label: 'Settings',
      items: [
        {
          icon: KeyRound,
          label: 'Credentials',
          path: '/app/s/credentials',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate:
            (can('apiKeys.view') || isRootAdmin) && (features.apiKey || features.apiAccessToken),
        },
        {
          icon: SlidersHorizontal,
          label: 'Preferences',
          path: '/app/s/preference-settings',
          show: (can('company.manage') || isRootSuperAdmin) && !isWhiteLabel,
        },
        {
          icon: Calendar,
          label: 'Operational Schedule',
          path: '/app/s/operational-schedule',
          show: (can('company.manage') || isRootAdmin) && !isWhiteLabel, // scio1: hidden for white-label
        },
        {
          icon: CheckSquare,
          label: 'Approval Requests',
          path: '/app/s/approval-list',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate: canApprove,
        },
      ],
    },
    {
      label: 'Data & Monitoring',
      items: [
        {
          icon: Database,
          label: 'Data Sources',
          path: '/app/s/datasources-admin',
          show: (can('dataSources.manage') || isTeamAdmin) && !removeOptiSignsBranding,
        },

        {
          icon: Rss,
          label: 'Feeds',
          path: '/app/s/feeds-admin',
          show: (can('feeds.manage') || isRootAdmin) && features.feedsCreate,
        },
        {
          icon: Link,
          label: 'Share Links',
          path: '/app/s/share-links-admin',
          show:
            (can('shareLinks.manage') || isTeamAdmin) &&
            features.shareLink &&
            !removeOptiSignsBranding,
        },
        {
          icon: BarChart3,
          label: 'Proof of Play',
          path: '/app/s/proof-of-play-settings',
          show: (can('proofOfPlay.manage') || isRootAdmin) && !hideProofofPlay, // scio1: gated on hideProofofPlay
        },
        {
          icon: Megaphone,
          label: 'Broadcast',
          path: '/app/s/broadcasts',
          // The page is native-only: with the flag off the route element is null and there is no
          // scio1 page behind this slug, so showing the link would lead to a blank screen.
          show: isRootAdmin && NATIVE_SETTINGS_SLUGS.has('broadcasts'),
        },
        {
          icon: Bell,
          label: 'Monitoring Alerts',
          path: '/app/s/monitoring-alert-rules',
          // scio1: hidden by hideMonitorAndAlert; on white-label also needs the alert-policy add-on.
          show:
            (can('monitoring.manage') || isRootAdmin) &&
            !hideMonitorAndAlert &&
            (!isWhiteLabel || features.alertPolicy),
        },
        {
          icon: ScrollText,
          label: 'Audit Log',
          path: '/app/security-audit-logging',
          // scio1: hidden by hideAuditLogging; on white-label also needs the audit-logging add-on.
          show:
            (can('audit.view') || isRootAdmin) &&
            !hideAuditLogging &&
            (!isWhiteLabel || features.securityAuditLogging),
        },
      ],
    },
    {
      label: 'Advanced',
      items: [
        {
          icon: Shield,
          label: 'Branding Settings',
          path: '/app/s/branding-settings',
          // scio1: hidden for white-label (removeOptiSignsBranding || isWhiteLabel). Page still
          // reachable by direct URL. Consistent with the profile dropdown (UserMenu).
          show: (can('branding.view') || isRootAdmin) && !isWhiteLabel,
        },
        {
          icon: Fingerprint,
          label: 'SAML Single Sign On',
          path: '/app/s/saml-settings',
          show: (can('sso.view') || isRootAdmin) && !isWhiteLabel,
        },
        {
          icon: Radio,
          label: 'External Communications',
          path: '/app/s/external-coms',
          // main base (isTeamAdmin) + white-label gate from this branch: hidden by
          // hideExternalCommunications; on white-label also needs the external-com add-on.
          show:
            (can('devices.manage') || isTeamAdmin) &&
            !hideExternalCommunications &&
            (!isWhiteLabel || features.externalCom),
        },
        {
          icon: MapPin,
          label: 'Functional Locations',
          path: '/app/s/functional-locations',
          // scio1 parity: host-scoped `account.domain.removeOptiSignsBranding` is not equivalent to scio3's account-scoped `me.brandingData`, so we gate on isWhiteLabel only.
          show: (can('functionalLocations.manage') || isRootAdmin) && !isWhiteLabel,
        },
        {
          icon: Key,
          label: 'API Keys',
          path: '/app/s/apikeys',
          // NOTE there are TWO API-key entries: this one and the hidden `Credentials` item above.
          // Wiring only one leaves the other admin-only — which is exactly what happened.
          show: (can('apiKeys.view') || isRootAdmin) && !isWhiteLabel,
        },
        {
          icon: Plug,
          label: 'Integrations',
          path: '/app/s/integrations',
          show: (can('integrations.manage') || isTeamAdmin) && !removeOptiSignsBranding,
        },
        {
          icon: FileSignature,
          label: 'Revue Workflow',
          path: '/app/s/revue',
          show: (can('revue.manage') || isRootAdmin) && features.contentApproval,
        },
        {
          icon: Tags,
          label: 'Tags',
          path: '/app/s/tags',
          show: canAdministerTags, // mutation surface — mirrors backend assertCanAdministerTags (team admin+)
        },
      ],
    },
  ];

  // Lapsed account: Manage Account keeps only the pages a paywalled user
  // needs (plan, payment, billing history, profile, company, members). Everything else is
  // hidden AND hard-gated — `show: false` alone is not enough, because the direct-URL check
  // in SettingsLayout only fires for items it can still find in the nav, so a dropped item
  // would let /app/s/coupons render. Items that survive keep their own `show`, so role and
  // white-label gating still applies on top.
  if (isAccountInactive) {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) =>
        LAPSED_SETTINGS_NAV_PATHS.includes(item.path)
          ? item
          : { ...item, show: false, accessGate: false },
      ),
    }));
  }

  return groups;
}

export function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navGroups = useSettingsNav();
  const features = useCompanyFeatures();
  const me = useAuthStore((s) => s.me);

  // Determine if current route is native or iframe
  const slug = location.pathname.replace('/app/s/', '');
  // Match both exact slug and sub-paths (e.g. "order-device/android-stick" matches "order-device")
  const isNativePage =
    NATIVE_SETTINGS_SLUGS.has(slug) ||
    (() => {
      for (const s of NATIVE_SETTINGS_SLUGS) {
        if (slug.startsWith(s + '/')) return true;
      }
      return false;
    })();

  // Access check: redirect users who don't have permission to view this settings page.
  // Build a map of path → NavItem for quick lookup.
  const currentNavItem = useMemo(() => {
    let prefixMatch: NavItem | null = null;
    for (const group of navGroups) {
      for (const item of group.items) {
        if (item.path === location.pathname) return item;
        // A SUB-PATH of a settings page is that page — `/app/s/users/:teamId` is the Users page
        // deep-linked to one teamspace. Exact matching alone left every such route ungated: the
        // lookup found nothing, so `isAccessDenied` was false and the page rendered to anyone with
        // the URL, which made the gate above trivially bypassable (#2351).
        //
        // The `/` boundary is load-bearing, not cosmetic: `/app/s/payment` is a plain string prefix
        // of `/app/s/payment-history`, and a bare `startsWith` would hand Payment Settings' gate to
        // Payment History. Longest match wins, so a future nested item beats its ancestor.
        if (
          location.pathname.startsWith(`${item.path}/`) &&
          item.path.length > (prefixMatch?.path.length ?? 0)
        ) {
          prefixMatch = item;
        }
      }
    }
    return prefixMatch;
  }, [navGroups, location.pathname]);

  // Redirect if the nav item exists but the user lacks permission.
  // accessGate (when defined) overrides show for direct-URL access control.
  const isAccessDenied =
    !!me &&
    !!currentNavItem &&
    (() => {
      if (currentNavItem.accessGate !== undefined) return !currentNavItem.accessGate;
      return currentNavItem.show === false;
    })();

  useEffect(() => {
    if (isAccessDenied) {
      toast.error(t('You do not have permission to access this page.'), {
        id: SETTINGS_ACCESS_DENIED_TOAST_ID,
      });
      navigate('/app/screenManagement', { replace: true });
    }
  }, [isAccessDenied, navigate, t]);

  // Plan gate: show upgrade screen instead of iframe when feature flag is off
  const planGateConfig = SETTINGS_PLAN_GATES[slug] ?? null;
  const isPlanGated = planGateConfig !== null && !features[planGateConfig.planFeature];

  // Resolve target scio1 state for the iframe
  const routeKey = location.pathname.replace('/app/', '');
  const targetState = SCIO1_STATE_MAP[routeKey];

  // Redirect scio1-only slugs that have no scio3 equivalent
  useEffect(() => {
    const target = SETTINGS_REDIRECTS[slug];
    if (target) navigate(target, { replace: true });
  }, [slug, navigate]);

  // Hide sidebar for pages that should be full-width (e.g. order-device is a standalone shop flow).
  // Matches the slug exactly or as a parent prefix so `order-device/<alias>` also hides.
  const hideSidebar =
    FULL_WIDTH_SETTINGS_SLUGS.has(slug) ||
    (() => {
      for (const s of FULL_WIDTH_SETTINGS_SLUGS) {
        if (slug.startsWith(s + '/')) return true;
      }
      return false;
    })();

  // Same prefix rule as hideSidebar, so a sub-route of a page that owns its scroll behaves the same.
  const pageOwnsScroll =
    PAGE_OWNS_SCROLL_SLUGS.has(slug) ||
    [...PAGE_OWNS_SCROLL_SLUGS].some((s) => slug.startsWith(s + '/'));

  // Don't render anything while redirecting due to access denial
  if (isAccessDenied) return null;

  return (
    <div data-settings className="flex h-full bg-muted">
      <aside className={cn('hidden w-60 shrink-0 lg:block', hideSidebar && 'lg:hidden')}>
        {/* `thumbClassName` is the only handle on the scrollbar here: Radix positions it
            absolutely over the full height of the ScrollArea, so it ran from the top-nav row to
            the bottom of the window and collided with the page tabs above it. Insetting the thumb
            (the only visible part — the track is transparent) keeps it clear of both ends. */}
        <ScrollArea className="h-full py-3" thumbClassName="my-3">
          <nav className="space-y-4 px-2">
            {navGroups.map((group) => {
              const visibleItems = group.items.filter((item) => item.show !== false);
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.label}>
                  <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {t(group.label)}
                  </h3>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const isActive =
                        location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/');
                      const Icon = item.icon;

                      // Slug-keyed testid for E2E targeting (e.g. settings-nav-provisioning-templates).
                      // Stable across i18n locale + label changes; safer than text-based locators.
                      // Use last URL segment so paths outside /app/s/ (e.g. Audit Log at
                      // /app/security-audit-logging) still produce a clean slug.
                      const slug = item.path.split('/').filter(Boolean).pop() ?? item.path;
                      return (
                        <Button
                          key={item.path}
                          variant="ghost"
                          size="sm"
                          data-testid={`settings-nav-${slug}`}
                          className={cn(
                            'w-full justify-start gap-2.5 rounded-md px-3 text-[13px] font-normal',
                            isActive
                              ? 'bg-sidebar-active font-medium text-foreground hover:bg-sidebar-active'
                              : 'text-muted-foreground hover:bg-sidebar-active/60 hover:text-foreground',
                          )}
                          onClick={() => navigate(item.path)}
                        >
                          {item.referralChip ? (
                            // Var D: gradient gift chip (~24px rounded square,
                            // white gift) instead of the plain monochrome icon.
                            // The gradient lives in `.ref-gift-chip` (referral
                            // overrides CSS) so it stays in sync with the
                            // dropdown card + widget hero.
                            <span className="ref-gift-chip flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] text-white shadow-[0_6px_12px_-6px_rgba(0,208,110,0.65)]">
                              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                            </span>
                          ) : (
                            <Icon
                              className={cn(
                                'h-4 w-4 shrink-0',
                                isActive ? 'text-primary' : 'text-muted-foreground/70',
                              )}
                            />
                          )}
                          <span className={cn(item.referralChip && 'text-foreground')}>
                            {t(item.label)}
                          </span>
                          {item.pill && (
                            <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10.5px] font-bold text-secondary-foreground">
                              {item.pill}
                            </span>
                          )}
                          {item.locked && (
                            // Paywall row (like Claude's locked "Groups · Enterprise"): a trailing lock
                            // + muted plan badge. The row still navigates; the page shows the upgrade.
                            <span className="ml-auto flex shrink-0 items-center gap-1.5 text-muted-foreground/70">
                              {item.lockBadge && (
                                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                  {t(item.lockBadge)}
                                </span>
                              )}
                              <Lock className="h-3 w-3" aria-label={t('Upgrade required')} />
                            </span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>
      {/* min-w-0 lets this flex item shrink below its content's intrinsic width on
          narrow screens, so pages with their own horizontal-scroll areas (e.g. the
          order-device pre-configure table) scroll internally instead of forcing the
          whole app wider than the viewport (which then just clips). No-op at the
          app's normal width. */}
      {/* No left padding: the sidebar's scrollbar runs right up to the white card instead of
          leaving a strip of grey between them (Jessica, 2026-10-14). Right/bottom keep 8px. */}
      <main
        className={cn(
          'flex min-h-0 min-w-0 flex-1 pr-2 pb-2',
          // Only beside the sidebar: when it is hidden (narrow window, or a page that hides it)
          // the card keeps its 8px from the window edge.
          hideSidebar ? 'pl-2' : 'pl-2 lg:pl-0',
        )}
      >
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-card">
          {/* Plan gate overlay — shown on top of iframe when feature flag is off */}
          {isPlanGated && planGateConfig && <PlanGate {...planGateConfig} />}
          {/* Native page content rendered via Outlet */}
          {isNativePage &&
            !isPlanGated &&
            (pageOwnsScroll ? (
              <Outlet />
            ) : (
              <ScrollContainer className="h-full">
                <Outlet />
              </ScrollContainer>
            ))}
          {/* Persistent iframe — h-full fills the flex-1 wrapper height */}
          <div
            className={cn(
              'min-h-0 flex-1 overflow-hidden',
              (isNativePage || isPlanGated) && 'hidden',
            )}
          >
            <Scio1Frame targetState={targetState} className="h-full w-full border-0" />
          </div>
        </div>
      </main>
    </div>
  );
}
